import { Json, createObj, enumObjProps } from './tools'
import { AppError, isAppError, throwInvalidParameter } from './errors'
import { Logger } from './logger'
import { ObservableAction, ObservableValue, createObservableAction, createObservableValue } from './observable'
import { Context, isContext } from './context'
import { TranslationService } from './translationService'
import { CoreDict, isTrCore } from './coreDicts'
import { Id } from './model'
import { IpcValueSerializer, deserializeValueFromJson, serializeValueToJson } from './ipcValueSerializer'

export function ipcBuildChannelName(serviceName: string, methodName: string, action?: string) {
  return action ? `${serviceName}:${methodName}:${action}` : `${serviceName}:${methodName}`
}

const logger = new Logger('IpcService')

enum Actions {
  SUBSCRIBE = 'subscribe',
}

interface ErrorResponse {
  message: string
  uid?: string
  code?: string
}

interface CallResponse {
  result?: unknown
  error?: ErrorResponse
}

/** Reduced copy of Electron.IpcRenderer interface */
export interface AppIpcRenderer {
  invoke(channel: string, ...args: unknown[]): Promise<unknown>
  on(channel: string, listener: (event: unknown, ...args: unknown[]) => void): void
}

/** Reduced copy of Electron.WebContents interface */
export interface AppWebContents {
  send(channel: string, ...args: unknown[]): void
}

/** Reduced copy of Electron.IpcMainInvokeEvent interface */
export interface AppIpcMainInvokeEvent {
  sender: AppWebContents
}

/** Reduced copy of Electron.IpcMain interface */
export interface AppIpcMain {
  handle(channel: string, listener: (event: AppIpcMainInvokeEvent, ...args: unknown[]) => Promise<void> | unknown): void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Func = (...args: any[]) => any

export type IpcServiceConstructor<T> = new (
  name: string,
  renderer: AppIpcRenderer,
  serializers: IpcValueSerializer[],
) => T

export class IpcService {
  constructor(
    private serviceName: string,
    private ipcRenderer: AppIpcRenderer,
    private serializers: IpcValueSerializer[],
  ) {}

  async invoke<T extends this, Name extends keyof T, Field = T[Name]>(
    method: Name,
    ...args: Field extends Func ? Parameters<Field> : never
  ): Promise<Field extends Func ? Awaited<ReturnType<Field>> : never> {
    logger.debug({}, '<5026502b> Invoke', { serviceName: this.serviceName, method, args })

    const clonedArgs = args.map((a: unknown) => serializeValueToJson(a, this.serializers))
    if (typeof method !== 'string') throwInvalidParameter('<ba6782fa>')
    const response = (await this.ipcRenderer.invoke(
      ipcBuildChannelName(this.serviceName, method),
      ...clonedArgs,
    )) as CallResponse
    const err = response.error
    if (err) {
      if (err.code) throw new AppError(err.code, err.message, { uid: err.uid })
      throw new Error(err.message)
    }
    logger.debug({}, '<638a05b0> Result', { result: response.result })
    const result = deserializeValueFromJson(response.result as Json, this.serializers)
    return result as Field extends Func ? Awaited<ReturnType<Field>> : never
  }

  handleObservableAction<T>(name: string): ObservableAction<T> {
    const action = createObservableAction<T>()
    // handle actions
    this.ipcRenderer.on(ipcBuildChannelName(this.serviceName, name), (event, ...args) =>
      action.emit(deserializeValueFromJson(args[0] as Json, this.serializers) as T),
    )
    // subscribe
    this.ipcRenderer
      .invoke(ipcBuildChannelName(this.serviceName, name, Actions.SUBSCRIBE))
      .catch(e => logger.error({}, '<4a4b8cbe> Error', { error: e }))
    return action
  }

  handleObservableValue<T>(name: string, initialValue: T): ObservableValue<T, Context> {
    const val = createObservableValue<T, Context>(initialValue)
    // subscribe and get initial value
    const subscribeChannel = ipcBuildChannelName(this.serviceName, name, Actions.SUBSCRIBE)
    this.ipcRenderer
      .invoke(subscribeChannel)
      .then(v => val.setValue(v as T))
      .catch(e => {
        logger.error({}, '<a36636a2>> Error', { error: e })
      })

    // handle changes
    const channel = ipcBuildChannelName(this.serviceName, name)
    logger.debug({}, `<e95df9d9> Subscribe to value change`, { subscribeChannel, channel })
    this.ipcRenderer.on(channel, (event, ...args) => {
      const value = deserializeValueFromJson(args[0] as Json, this.serializers) as T
      const ctx = deserializeValueFromJson(args[1] as Json, this.serializers) as Context | undefined
      logger.debug(ctx ?? {}, `<d7413e2a> Value changed`, { channel, value: value ?? null })
      val.setValue(value, ctx)
    })

    return val
  }

  handleOptionalObservableValue<T>(name: string, initialValue?: T): ObservableValue<T | undefined, Context> {
    return this.handleObservableValue<T | undefined>(name, initialValue)
  }
}

function createErrorResponse(ctx: Context, e: unknown, translationService: TranslationService<CoreDict>): CallResponse {
  if (isAppError(e)) {
    let message: string
    if (isTrCore(e.messageWithoutUid)) {
      message = translationService.tr(ctx, e.messageWithoutUid)
    } else {
      message = e.messageWithoutUid
    }
    return <CallResponse>{ error: { code: e.code, message, uid: e.uid } }
  } else if (e instanceof Error) {
    return <CallResponse>{ error: { message: e.message } }
  } else {
    return <CallResponse>{ error: { message: (e as object).toString() } }
  }
}

function getErrorMessage(e: unknown): string {
  return e instanceof Error ? e.message : (e as object).toString()
}

type UserIdProvider = () => Id | undefined

let currentUserIdProvider: UserIdProvider | undefined

export function ipcSetCurrentUserIdProvider(p: UserIdProvider) {
  currentUserIdProvider = p
}

/** Handle incoming requests from Electron frontend and execute corresponding methods of target service on the
 * backend
 *
 * @param serviceName Name of service
 * @param service Some service which methods will be called
 **/
export function ipcHandleService(
  handler: AppIpcMain,
  translationService: TranslationService<CoreDict>,
  serviceName: string,
  service: object,
  serializers: IpcValueSerializer[],
) {
  logger.debug({}, '<9e83c8ea> Handle service', { serviceName })
  const fields = enumObjProps(service)
  fields.forEach(field => {
    const value = field.descriptor.value
    if (typeof value === 'function') {
      handleMethod(translationService, handler, service, serviceName, field.name, value, serializers)
    } else if (typeof value === 'object' && value instanceof ObservableAction) {
      handleObservableAction(handler, serviceName, field.name, value, serializers)
    } else if (typeof value === 'object' && value instanceof ObservableValue) {
      handleObservableValue(handler, serviceName, field.name, value, serializers)
    }
  })
}

function handleMethod(
  translationService: TranslationService<CoreDict>,
  handler: AppIpcMain,
  service: object,
  serviceName: string,
  methodName: string,
  method: (...args: unknown[]) => unknown,
  serializers: IpcValueSerializer[],
) {
  handler.handle(ipcBuildChannelName(serviceName, methodName), (event, ...args: unknown[]) => {
    // try to get context from first argument

    const deserializedArgs = args.map(v => deserializeValueFromJson(v as Json, serializers))

    let ctx: Context = {}
    const firstArg = deserializedArgs.length > 0 ? deserializedArgs[0] : undefined
    if (isContext(firstArg)) {
      ctx = firstArg
      if (!ctx.userId) {
        ctx = {
          ...ctx,
          userId: currentUserIdProvider?.(),
        }
        deserializedArgs[0] = ctx
      }
    }
    logger.debug(ctx, '<84a79e2e> Invoke method', { serviceName, methodName, deserializedArgs })

    try {
      const result = method.apply(service, deserializedArgs)
      if (result instanceof Promise) {
        return result
          .then(result => {
            logger.debug(ctx, '<b613a926> Invoke result', { serviceName, methodName, result })
            return createObj<CallResponse>({ result: serializeValueToJson(result, serializers) })
          })
          .catch(error => {
            logger.error({}, `<6357af79> Invoke error: ${getErrorMessage(error)}`, { error })
            return createErrorResponse(ctx, error, translationService)
          })
      } else {
        logger.debug(ctx, '<1ed0e1b4> Invoke result', { serviceName, methodName, result })
        return createObj<CallResponse>({ result: serializeValueToJson(result, serializers) })
      }
    } catch (error) {
      logger.error({}, `<24c0b057> Invoke error: ${getErrorMessage(error)}`)
      return createErrorResponse(ctx, error, translationService)
    }
  })
}

function handleObservableAction(
  handler: AppIpcMain,
  serviceName: string,
  name: string,
  observable: ObservableAction<unknown>,
  serializers: IpcValueSerializer[],
) {
  const subscriptions: AppWebContents[] = []

  // handle subscription
  handler.handle(ipcBuildChannelName(serviceName, name, Actions.SUBSCRIBE), e => subscriptions.push(e.sender))

  // send action to subscribers
  const channel = ipcBuildChannelName(serviceName, name)
  observable.subscribe(v => subscriptions.forEach(sender => sender.send(channel, serializeValueToJson(v, serializers))))
}

function handleObservableValue(
  handler: AppIpcMain,
  serviceName: string,
  name: string,
  observable: ObservableValue<unknown, Context>,
  serializers: IpcValueSerializer[],
) {
  const subscriptions: AppWebContents[] = []
  // handle subscription and send initial value as result
  const subscribeChannel = ipcBuildChannelName(serviceName, name, Actions.SUBSCRIBE)
  handler.handle(subscribeChannel, e => {
    logger.debug({}, '<22b00c87> Subscribe to ObservableValue and send current value', {
      subscribeChannel,
      name,
      value: observable.value ?? null,
    })
    subscriptions.push(e.sender)
    return serializeValueToJson(observable.value, serializers)
  })

  // send action to subscribers
  const channel = ipcBuildChannelName(serviceName, name)
  observable.subscribe((v, ctx) => {
    logger.debug(ctx ?? {}, `<3497e680> Notify observers about changed value`, {
      channel,
      subscriptions: subscriptions.length,
      value: v ?? null,
    })
    subscriptions.forEach(sender =>
      sender.send(channel, serializeValueToJson(v, serializers), serializeValueToJson(ctx, serializers)),
    )
  })
}
